import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import i18n from "../i18n";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Gets the user's locale currency code based on their region
 * Falls back to USD if unable to determine
 */
export function getUserCurrencyCode(): string {
  try {
    // First try to detect region from timezone
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Map timezones to currency codes
    if (timeZone) {
      // European timezones
      if (timeZone.startsWith("Europe/")) {
        return "EUR";
      }
      // UK timezones
      if (timeZone === "Europe/London") {
        return "GBP";
      }
      // Nigerian timezone
      if (timeZone === "Africa/Lagos") {
        return "NGN";
      }
      // US timezones
      if (timeZone === "America/New_York") {
        return "USD";
      }

      // Add more timezone-based mappings as needed
    }

    // If timezone detection doesn't give us enough info, fall back to language-based detection
    const currentLanguage = i18n.language;

    const currencyMap: Record<string, string> = {
      de: "EUR",
      fr: "EUR",
      es: "EUR",
      "en-GB": "GBP",
      "en-EU": "EUR",
    };

    // Try to get currency from the language map
    const mappedCurrency = currencyMap[currentLanguage];
    if (mappedCurrency) {
      return mappedCurrency;
    }

    // Try to get from NumberFormat
    const formatter = new Intl.NumberFormat(currentLanguage, {
      style: "currency",
      currency: "USD",
    });
    const currency = formatter.resolvedOptions().currency;
    if (currency && currency !== "USD") {
      return currency;
    }

    return "USD"; // Final fallback
  } catch {
    return "USD";
  }
}

/**
 * Formats a number as currency using the user's locale settings
 * @param amount - The number to format
 * @param options - Optional Intl.NumberFormat options to override defaults
 */
export function formatCurrency(
  amount: number,
  options?: Partial<Intl.NumberFormatOptions>
): string {
  try {
    const locale = navigator.language;
    const currencyCode = getUserCurrencyCode();
    const formattedCurrency = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      ...options,
    }).format(amount);
    return formattedCurrency;
  } catch (error) {
    // Fallback to basic USD formatting if something goes wrong
    console.error("Currency formatting error:", error);
    return `$${amount.toFixed(2)}`;
  }
}
